import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Mail, ArrowLeft } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { api, ApiError, type ProviderFlags } from '../api';

type Step = 'email' | 'password' | 'otp';

const ERRORS: Record<string, string> = {
  no_account: 'No Pavlov account is linked to that login. Ask an admin to invite you.',
  oauth_failed: 'Sign-in failed. Please try again.',
  oauth_denied: 'Sign-in was cancelled.',
  provider_unavailable: 'That sign-in method isn’t enabled yet.',
};

// Monochrome marks (theme brief: stay monochrome; provider glyphs in currentColor).
function GoogleIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 11v2.8h3.9c-.16.9-1.1 2.6-3.9 2.6a4.4 4.4 0 1 1 0-8.8c1.3 0 2.1.5 2.6 1l1.9-1.8A6.9 6.9 0 1 0 12 19a6.6 6.6 0 0 0 6.7-6.9c0-.5 0-.8-.1-1.1H12z"
        fill="currentColor"
      />
    </svg>
  );
}
function CloudflareIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M16.5 16H7a3 3 0 1 1 .6-5.9A4.5 4.5 0 0 1 16 11a3 3 0 0 1 .5 5z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function LoginPage() {
  const { login, loginWithOtp } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(ERRORS[params.get('error') ?? ''] ?? null);
  const [busy, setBusy] = useState(false);
  const [providers, setProviders] = useState<ProviderFlags>({
    password: true,
    otp: true,
    google: false,
    cloudflare: false,
  });

  useEffect(() => {
    api
      .providers()
      .then(setProviders)
      .catch(() => {});
  }, []);

  function clearErrorParam() {
    if (params.get('error')) {
      params.delete('error');
      setParams(params, { replace: true });
    }
  }
  function fail(err: unknown, fallback: string) {
    setError(
      err instanceof ApiError && err.status === 429
        ? 'Too many attempts. Try again later.'
        : fallback,
    );
  }

  function continueEmail(e: FormEvent) {
    e.preventDefault();
    setError(null);
    clearErrorParam();
    if (email.includes('@')) setStep('password');
    else setError('Enter a valid email.');
  }
  async function signIn(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email, password);
      navigate('/', { replace: true });
    } catch (err) {
      fail(err, 'Invalid email or password.');
    } finally {
      setBusy(false);
    }
  }
  async function sendCode() {
    setError(null);
    setBusy(true);
    try {
      await api.otpRequest(email);
      setStep('otp');
    } catch (err) {
      fail(err, 'Couldn’t send a code. Try again.');
    } finally {
      setBusy(false);
    }
  }
  async function verifyCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await loginWithOtp(email, code.trim());
      navigate('/', { replace: true });
    } catch (err) {
      fail(err, 'Invalid or expired code.');
    } finally {
      setBusy(false);
    }
  }
  function oauth(provider: 'google' | 'cloudflare') {
    window.location.href = api.oauthStartUrl(provider);
  }

  const hasSocial = providers.google || providers.cloudflare;
  const label = 'mb-1.5 block eyebrow';

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center bg-paper px-4">
      <a
        href="https://runpavlov.ai"
        className="absolute left-6 top-6 text-sm font-medium text-muted transition-colors hover:text-ink"
      >
        ← Back to runpavlov.ai
      </a>

      <div className="w-full max-w-[420px] animate-fade-in rounded-card border border-line bg-paper p-8">
        <div className="mb-6 text-center">
          <div className="font-display text-2xl font-medium tracking-tight text-ink">Pavlov.</div>
          <div className="eyebrow mt-5">Sign in</div>
          <h1 className="mt-1 text-[26px] text-ink">
            {step === 'otp' ? 'Enter your code' : 'Welcome back'}
          </h1>
          {step === 'otp' && (
            <p className="mt-1.5 text-sm text-muted">We sent a code to {email}.</p>
          )}
        </div>

        {error && (
          <div className="mb-4 rounded-input border border-ink/15 bg-ink/[0.03] px-3 py-2 text-sm text-ink">
            {error}
          </div>
        )}

        {step === 'email' && (
          <>
            <form onSubmit={continueEmail} className="space-y-4">
              <div>
                <label className={label}>Email</label>
                <input
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input"
                  autoComplete="username"
                  autoFocus
                  required
                />
              </div>
              <button type="submit" className="btn-primary w-full">
                Continue
              </button>
            </form>

            {hasSocial && (
              <>
                <div className="my-5 flex items-center gap-3 text-xs text-muted">
                  <span className="h-px flex-1 bg-line" /> or{' '}
                  <span className="h-px flex-1 bg-line" />
                </div>
                <div className="space-y-2">
                  {providers.google && (
                    <button onClick={() => oauth('google')} className="btn-outline w-full">
                      <GoogleIcon /> Continue with Google
                    </button>
                  )}
                  {providers.cloudflare && (
                    <button onClick={() => oauth('cloudflare')} className="btn-outline w-full">
                      <CloudflareIcon /> Continue with Cloudflare
                    </button>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {step === 'password' && (
          <form onSubmit={signIn} className="space-y-4">
            <button
              type="button"
              onClick={() => setStep('email')}
              className="flex items-center gap-1 text-xs text-muted hover:text-ink"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> {email}
            </button>
            <div>
              <label className={label}>Password</label>
              <input
                type="password"
                placeholder="••••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                autoComplete="current-password"
                autoFocus
                required
              />
            </div>
            <button type="submit" disabled={busy} className="btn-primary w-full">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
            </button>
            <button
              type="button"
              onClick={() => void sendCode()}
              disabled={busy}
              className="flex w-full items-center justify-center gap-2 text-xs font-medium text-muted hover:text-ink"
            >
              <Mail className="h-3.5 w-3.5" /> Email me a one-time code instead
            </button>
          </form>
        )}

        {step === 'otp' && (
          <form onSubmit={verifyCode} className="space-y-4">
            <button
              type="button"
              onClick={() => setStep('password')}
              className="flex items-center gap-1 text-xs text-muted hover:text-ink"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </button>
            <div>
              <label className={label}>6-digit code</label>
              <input
                inputMode="numeric"
                placeholder="······"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="input text-center text-lg tracking-[0.5em]"
                autoFocus
                required
              />
            </div>
            <button type="submit" disabled={busy} className="btn-primary w-full">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Verify &amp; sign in
            </button>
            <button
              type="button"
              onClick={() => void sendCode()}
              disabled={busy}
              className="w-full text-xs font-medium text-muted hover:text-ink"
            >
              Resend code
            </button>
          </form>
        )}
      </div>

      <p className="mt-6 text-center text-xs text-muted">
        Pavlov · phishing-simulation engine ·{' '}
        <a href="https://runpavlov.ai" className="hover:text-ink">
          runpavlov.ai
        </a>
      </p>
    </div>
  );
}
